import { apiConfig } from '@nomiss/configs';
import { buildApiHeaders, fetchWithAuthRecovery } from './sdk-client';
import { ApiRequestError } from './api-request.util';

export class AdminSettingsError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminSettingsError');
  }
}

export type AdminSetting = {
  key: string;
  category: string;
  name: string;
  description: string | null;
  dataType: string;
  editable: boolean;
  value: string | null;
};

export type UpdateSettingResult = {
  message: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function parseAdminSetting(value: unknown): AdminSetting | null {
  if (!isRecord(value) || typeof value.key !== 'string') return null;
  return {
    key: value.key,
    category: asString(value.category),
    name: asString(value.name),
    description:
      typeof value.description === 'string' ? value.description : null,
    dataType: asString(value.dataType),
    editable: value.editable !== false,
    value: typeof value.value === 'string' ? value.value : null,
  };
}

function parseErrorMessage(json: unknown, fallback: string): string {
  if (!isRecord(json)) return fallback;
  const rawMessage = json.message;
  if (Array.isArray(rawMessage)) return rawMessage.join(', ');
  if (typeof rawMessage === 'string') return rawMessage;
  return fallback;
}

export async function listSettings(): Promise<AdminSetting[]> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/settings`,
    {
      headers: buildApiHeaders(),
    },
  );

  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as unknown;
    throw new AdminSettingsError(
      response.status,
      parseErrorMessage(json, 'Failed to load settings'),
    );
  }

  const json = (await response.json()) as unknown;
  const envelope = isRecord(json) ? json : {};
  if (!Array.isArray(envelope.data)) return [];
  return envelope.data
    .map(parseAdminSetting)
    .filter((item): item is AdminSetting => item != null);
}

export async function updateSetting(
  key: string,
  value: string,
): Promise<UpdateSettingResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/settings/${encodeURIComponent(key)}`,
    {
      method: 'PUT',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ value }),
    },
  );

  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as unknown;
    throw new AdminSettingsError(
      response.status,
      parseErrorMessage(json, 'Failed to update setting'),
    );
  }

  const json = (await response.json()) as unknown;
  const envelope = isRecord(json) ? json : {};
  return {
    message: asString(envelope.message, 'Setting updated'),
  };
}
